import React, { useCallback, useEffect, useRef, useState } from 'react';
import InvoiceDialog from './InvoiceDialog';
import { getProductMappings, getMappingProducts, saveProductMappings } from '../services/productMappingApi';
import { mappingStatus, productIndex, referenceId, validMappedProduct } from '../utils/productMapping';

const button = 'border rounded px-3 py-1.5 text-sm disabled:opacity-50';
const errorText = error => error.response?.data?.error || 'Unable to load or save mappings. Please try again.';
export default function ProductMappingPanel({ invoiceId, onClose, onChanged, onCatalogChanged, onNext }) {
  const [doc, setDoc] = useState(null);
  const [products, setProducts] = useState([]);
  const [catalogReady, setCatalogReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [draft, setDraft] = useState({});
  const [editing, setEditing] = useState({});
  const [search, setSearch] = useState({});
  const [confirmed, setConfirmed] = useState({});
  const callbacks = useRef({ onChanged, onCatalogChanged });
  callbacks.current = { onChanged, onCatalogChanged };
  const accept = useCallback(data => {
    setDoc(data); setDraft({}); setEditing({}); setSearch({}); setConfirmed({});
    callbacks.current.onChanged(data);
  }, []);
  const acceptCatalog = useCallback((organization, data) => {
    if (!Array.isArray(data)) throw new Error('Invalid Product Master response');
    setProducts(data); setCatalogReady(true);
    callbacks.current.onCatalogChanged?.(organization, data);
  }, []);
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    (async () => {
      try {
        const data = await getProductMappings(invoiceId, { signal: controller.signal });
        if (!active) return;
        accept(data);
        if (!['COG', 'The Bengal Craft'].includes(data.organization)) return;
        const catalog = await getMappingProducts(data.organization, { signal: controller.signal });
        if (active) acceptCatalog(data.organization, catalog);
      } catch (err) { if (active) setError(errorText(err)); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; controller.abort(); };
    // accept uses current callbacks via a ref; reload only when invoice changes.
  }, [invoiceId, accept, acceptCatalog]);

  async function refreshProducts() {
    if (lock.current || !doc) return;
    lock.current = true; setLoading(true); setError(''); setMessage('');
    try { acceptCatalog(doc.organization, await getMappingProducts(doc.organization)); }
    catch (err) { setCatalogReady(false); setError(errorText(err)); }
    finally { lock.current = false; setLoading(false); }
  }
  async function reloadInvoice() {
    if (lock.current) return;
    lock.current = true; setLoading(true); setError(''); setMessage('');
    try {
      const data = await getProductMappings(invoiceId); accept(data);
      if (['COG', 'The Bengal Craft'].includes(data.organization)) acceptCatalog(data.organization, await getMappingProducts(data.organization));
    } catch (err) { setCatalogReady(false); setError(errorText(err)); }
    finally { lock.current = false; setLoading(false); }
  }

  const index = productIndex(products);
  const changes = (doc?.items || []).filter(item => draft[item._id] && referenceId(draft[item._id]) !== referenceId(item.productId));
  const canSave = !!doc?.canMapProducts && catalogReady && changes.length > 0 && changes.every(item => {
    const selected = index.get(referenceId(draft[item._id]));
    return selected?.isActive === true && selected.organization === doc.organization && (!item.productId || confirmed[item._id]);
  });
  async function save(event) {
    event.preventDefault();
    if (lock.current || loading || !canSave) return;
    lock.current = true; setBusy(true); setError(''); setMessage('');
    const payload = { expectedVersion: doc.__v ?? 0, mappings: changes.map(item => ({ itemId: item._id,
      productId: draft[item._id], ...(item.productId ? { confirmRemap: true } : {}) })) };
    try {
      accept(await saveProductMappings(invoiceId, payload));
      setMessage('Product mappings saved. Original invoice and delivery information is unchanged.');
    } catch (err) {
      if (err.response?.status === 409) {
        setDraft({}); setEditing({}); setConfirmed({});
        try {
          const latest = await getProductMappings(invoiceId); accept(latest);
          acceptCatalog(latest.organization, await getMappingProducts(latest.organization));
          setError('The invoice changed. Mappings were reloaded; review and select products again before saving.');
        } catch { setDoc(null); setCatalogReady(false); setError('The invoice changed and reload failed. Reload invoice before selecting products again.'); }
      } else setError(`${errorText(err)} Reload invoice to check the latest mappings if the save outcome is uncertain.`);
    } finally { lock.current = false; setBusy(false); }
  }
  const disabled = loading || busy;
  return <InvoiceDialog title="Map Products" onClose={onClose} busy={busy}>
    <div className="flex flex-wrap gap-2 mb-4">
      <button className={button} disabled={disabled} onClick={reloadInvoice}>Reload invoice</button>
      <button className={button} disabled={disabled || !doc || !['COG', 'The Bengal Craft'].includes(doc.organization)} onClick={refreshProducts}>Refresh available products</button>
      <a className={button} href="/products" target="_blank" rel="noopener noreferrer">Open Product Master</a>
      {onNext && <button className={button} disabled={disabled || changes.length > 0} onClick={onNext}>Next Incomplete Invoice</button>}
    </div>
    {loading && <p role="status">Loading product mappings…</p>}
    {error && <p role="alert" className="bg-rose-50 text-rose-800 p-3 rounded mb-3">{error}</p>}
    {message && <p role="status" className="bg-emerald-50 text-emerald-800 p-3 rounded mb-3">{message}</p>}
    {doc && <>
      <p className="mb-2 font-medium">{doc.invoiceNumber || doc._id} · {doc.customerName} · {doc.organization || 'Unknown organization'}</p>
      <p className="mb-3 text-sm">Mapping status: {mappingStatus(doc, products, catalogReady)}</p>
      <p className="text-sm text-gray-600 mb-4">Select products explicitly; descriptions are never matched automatically. Create a missing product in Product Master, return here and refresh the list. Archived existing mappings are retained; new selections must be active.</p>
      {!doc.canMapProducts && <p role="alert" className="bg-amber-50 text-amber-900 p-3 rounded mb-3">{doc.mappingIssues?.join(' ') || 'This invoice requires stable, unique item IDs before mapping.'}</p>}
      <form onSubmit={save}>
        <fieldset disabled={disabled || !doc.canMapProducts}>
          <div className="overflow-x-auto"><table className="w-full text-sm" aria-label="Invoice product mappings">
            <thead className="bg-gray-50"><tr>{['Historical item', 'Product Master', 'Status'].map(label => <th key={label} className="px-3 py-2 text-left">{label}</th>)}</tr></thead>
            <tbody>{doc.items.map((item, position) => {
              const valid = catalogReady ? validMappedProduct(item, doc.organization, index) : item.mappingValid ? item.currentProduct : null;
              const current = item.currentProduct;
              const editable = !item.productId || editing[item._id];
              const term = (search[item._id] || '').trim().toLowerCase();
              const options = products.filter(product => product.organization === doc.organization && product.isActive === true &&
                (referenceId(product._id) === referenceId(draft[item._id]) || `${product.name} ${product.sku}`.toLowerCase().includes(term)));
              return <tr key={`${item._id || 'legacy'}-${position}`} className="border-t">
                <td className="px-3 py-2 align-top">{item.label || 'Unnamed historical item'}</td>
                <td className="px-3 py-2">
                  {item.productId && <div className="mb-2">{current ? `${current.name} — ${current.sku}` : `Unavailable reference: ${item.productId}`}{current?.isActive === false && ' (Archived)'}
                    {!editable && <button type="button" className="text-indigo-700 underline ml-2" disabled={!catalogReady} onClick={() => setEditing(old => ({ ...old, [item._id]: true }))}>Change Mapping<span className="sr-only"> {item.label}</span></button>}
                  </div>}
                  {editable && <div className="space-y-2">
                    <input className="w-full border px-2 py-1 rounded" aria-label={`Search products for ${item.label}`} placeholder="Search by name or SKU" disabled={!catalogReady}
                      value={search[item._id] || ''} onChange={event => setSearch(old => ({ ...old, [item._id]: event.target.value }))} />
                    <select aria-label={`Product for ${item.label}`} className="w-full border px-2 py-1 rounded" disabled={!catalogReady} value={draft[item._id] || ''}
                      onChange={event => { setDraft(old => ({ ...old, [item._id]: event.target.value })); setConfirmed(old => ({ ...old, [item._id]: false })); }}>
                      <option value="">Select Product</option>
                      {options.map(product => <option key={product._id} value={product._id}>{product.name} — {product.sku}</option>)}
                    </select>
                    {!options.length && <p className="text-xs text-gray-500">No matching active products. Open Product Master or adjust the search.</p>}
                    {item.productId && draft[item._id] && referenceId(draft[item._id]) !== referenceId(item.productId) && <label className="flex items-center gap-2 text-xs text-amber-900">
                      <input type="checkbox" checked={!!confirmed[item._id]} onChange={event => setConfirmed(old => ({ ...old, [item._id]: event.target.checked }))} />I deliberately want to replace this item’s existing product mapping.
                    </label>}
                  </div>}
                </td>
                <td className="px-3 py-2 align-top">{valid ? <span className="text-emerald-800">Mapped</span> : item.productId ? <span className="text-amber-800">Invalid mapping</span> : <span className="text-gray-600">Unmapped</span>}{draft[item._id] && referenceId(draft[item._id]) !== referenceId(item.productId) && <div className="text-xs text-indigo-700">Selection not yet saved</div>}</td>
              </tr>;
            })}</tbody>
          </table></div>
          <button type="submit" className="mt-4 bg-indigo-600 text-white px-4 py-2 rounded disabled:opacity-50" disabled={!canSave}>Save Mappings</button>
        </fieldset>
      </form>
    </>}
  </InvoiceDialog>;
}
